import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function SafetyPage() {
  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-[72px]">
        <section className="w-full py-16 md:py-24 bg-background-50">
          <div className="px-4 md:px-6 max-w-3xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-8">
              <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700">Safety</span>
            </nav>

            <h1 className="text-4xl md:text-5xl font-heading font-bold text-foreground-950 mb-6">
              Kakobuy Safety Guide
            </h1>

            <p className="text-lg text-foreground-600 leading-relaxed mb-10">
              Practical guidance for safe online shopping and product research. Learn how to verify products, understand risks, and protect yourself as a buyer.
            </p>

            <div className="prose-custom space-y-8">
              <div className="bg-background-100 rounded-lg border border-background-200/70 p-6">
                <h2 className="text-xl font-heading font-semibold text-foreground-950 mb-4">Before Using Any Spreadsheet</h2>
                <div className="space-y-4">
                  {[
                    { title: 'Verify Product Details', desc: 'Always click through to the actual product page to confirm current pricing, availability, size options, and color choices before purchasing.' },
                    { title: 'Check Seller Information', desc: 'Review seller ratings, transaction history, and buyer feedback. Established sellers with consistent positive reviews are generally more reliable.' },
                    { title: 'Understand Shipping Costs', desc: 'Shipping fees can vary significantly based on destination, weight, and shipping method. Calculate total costs including shipping before committing to a purchase.' },
                    { title: 'Research Import Regulations', desc: 'Familiarize yourself with your country\'s import duties, taxes, and restricted items. Some products may require additional documentation or fees.' },
                  ].map((item) => (
                    <div key={item.title} className="flex gap-3">
                      <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center text-primary-500 mt-0.5">
                        <i className="ri-checkbox-circle-line"></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-900 mb-1">{item.title}</h3>
                        <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <h2 className="text-2xl font-heading font-semibold text-foreground-950 mt-10 mb-4">Safe Shopping Checklist</h2>
              <div className="space-y-3">
                {[
                  'Verify the current price on the actual product page',
                  'Check seller ratings and review history',
                  'Read product descriptions carefully for material and size details',
                  'Calculate total cost including shipping and potential import fees',
                  'Review return and refund policies before purchasing',
                  'Keep records of your order confirmation and communication',
                  'Use secure payment methods with buyer protection',
                  'Start with small orders when trying a new seller',
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs font-bold flex items-center justify-center">
                      {idx + 1}
                    </span>
                    <p className="text-sm text-foreground-600 leading-relaxed pt-0.5">{item}</p>
                  </div>
                ))}
              </div>

              <h2 className="text-2xl font-heading font-semibold text-foreground-950 mt-10 mb-4">Common Mistakes When Using Product Spreadsheets</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { title: 'Trusting Outdated Prices', desc: 'Prices change frequently. Always verify on the live product page.' },
                  { title: 'Ignoring Size Charts', desc: 'Sizing varies by brand and region. Always consult the size guide.' },
                  { title: 'Skipping Quality Notes', desc: 'Previous buyer notes contain valuable real-world experience.' },
                  { title: 'Rushing Purchases', desc: 'Take time to compare options and verify details before buying.' },
                ].map((item) => (
                  <div key={item.title} className="p-4 bg-background-100 rounded-lg border border-background-200/70">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-5 h-5 flex items-center justify-center text-accent-600">
                        <i className="ri-error-warning-line text-sm"></i>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900">{item.title}</h3>
                    </div>
                    <p className="text-xs text-foreground-500 leading-relaxed pl-7">{item.desc}</p>
                  </div>
                ))}
              </div>

              <h2 className="text-2xl font-heading font-semibold text-foreground-950 mt-10 mb-4">How To Verify Product Information</h2>
              <p className="text-base text-foreground-600 leading-relaxed">
                Cross-referencing is your best tool for verification. When you find an interesting product in a spreadsheet, take these extra steps: search for the same product on multiple platforms to compare prices and availability, look for buyer review photos rather than relying solely on seller images, check the seller's overall store rating and read through recent feedback, and if possible, reach out to the seller with questions before purchasing to gauge their responsiveness and professionalism.
              </p>

              <div className="mt-10 p-6 bg-accent-50 rounded-lg border border-accent-200/50">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center text-accent-600 mt-0.5">
                    <i className="ri-information-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-accent-800 mb-2">Important Notice</h4>
                    <p className="text-sm text-accent-700 leading-relaxed">
                      This website provides informational resources only. Product availability, prices and policies may change. We do not process transactions or hold inventory. Always exercise caution and conduct your own research before making online purchases.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-background-200/70">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                >
                  <i className="ri-arrow-left-line"></i>
                  Back to Homepage
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Kakobuy Safety Guide',
            description: 'Practical guidance for safe online shopping and product research.',
            url: 'https://kakobuyfinds.bond/safety',
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://kakobuyfinds.bond/' },
              { '@type': 'ListItem', position: 2, name: 'Safety Guide', item: 'https://kakobuyfinds.bond/safety' },
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'What should I check before using any product spreadsheet?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Always verify product details by clicking through to the actual product page to confirm current pricing, availability, size options, and color choices. Check seller ratings, transaction history, and buyer feedback. Calculate total costs including shipping before committing. Research your country\'s import regulations and potential duties.',
                },
              },
              {
                '@type': 'Question',
                name: 'How do I verify product information before purchasing?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Cross-reference products across multiple platforms to compare prices and availability. Look for buyer review photos rather than relying solely on seller images. Check the seller\'s overall store rating and read through recent feedback. Reach out to the seller with questions before purchasing to gauge responsiveness.',
                },
              },
              {
                '@type': 'Question',
                name: 'What are common mistakes when using product spreadsheets?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Common mistakes include trusting outdated prices without verifying on live product pages, ignoring size charts as sizing varies by brand and region, skipping quality notes from previous buyers, and rushing purchases without comparing options. Always take time to verify details before buying.',
                },
              },
              {
                '@type': 'Question',
                name: 'Is it safe to buy products found through spreadsheets?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Spreadsheets are discovery tools, not purchasing guarantees. Safety depends on verifying product information yourself. Use secure payment methods with buyer protection, start with small orders when trying new sellers, keep records of order confirmations, and review return and refund policies before purchasing.',
                },
              },
              {
                '@type': 'Question',
                name: 'What should I know about international shipping and import regulations?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Shipping costs vary significantly by destination, weight, and shipping method. Calculate total costs including shipping and potential import duties before purchasing. Familiarize yourself with your country\'s import regulations, as some products may require additional documentation or incur extra fees at customs.',
                },
              },
            ],
          }),
        }}
      />
    </>
  );
}